class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.neoniifyBound === 'true') return;
    overlay.dataset.neoniifyBound = 'true';
    overlay.addEventListener('click', () => this.close());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.neoniifyDrawerBound === 'true') return;

    cartLink.dataset.neoniifyDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner == null) return;
      sectionElement.innerHTML = inner;
    });

    this.bindOverlay();
    this.querySelector('cart-drawer-items')?.syncCartChrome?.(parsedState);

    if (typeof window.scheduleNeoniifyCartPreviews === 'function') {
      window.scheduleNeoniifyCartPreviews(this);
    }

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = (selector && parsed.querySelector(selector)) || parsed.querySelector('.shopify-section');
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  cartJsUrl() {
    const cartUrl = window.routes?.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  cartPageUrl() {
    return (window.routes?.cart_url || '/cart').replace(/\.js$/, '');
  }

  async fetchDrawerSectionHtml() {
    const urls = [
      `${this.cartPageUrl()}?section_id=cart-drawer`,
      `${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`,
    ];
    const pathname = window.location.pathname;
    if (pathname && !urls.some((url) => url.startsWith(`${pathname}?`))) {
      urls.push(`${pathname}?section_id=cart-drawer`);
    }

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const html = await response.text();
        if (html && /id=["']CartDrawer["']/.test(html)) return html;
      } catch (error) {
        console.error('Neoniify cart drawer section fetch failed:', url, error);
      }
    }

    return '';
  }

  async refreshFromShopify({ open = true } = {}) {
    const [html, cartResponse] = await Promise.all([
      this.fetchDrawerSectionHtml(),
      fetch(this.cartJsUrl(), { headers: { Accept: 'application/json' } }),
    ]);

    if (!html) {
      console.error('Neoniify cart drawer section was not returned from section_id=cart-drawer');
      if (open && typeof this.open === 'function') this.open();
      return null;
    }

    const cart = cartResponse.ok ? await cartResponse.json().catch(() => ({})) : {};
    this.classList.toggle('is-empty', Number(cart.item_count) === 0);
    const parsedState = { ...cart, sections: { 'cart-drawer': html } };
    if (open) this.renderContents(parsedState);
    else {
      const itemsSource = this.getSectionInnerHTML(html, '#CartDrawer-CartItems');
      const itemsTarget = document.getElementById('CartDrawer-CartItems');
      if (itemsSource != null && itemsTarget) itemsTarget.innerHTML = itemsSource;
      const footerSource = this.getSectionInnerHTML(html, '#CartDrawer-Footer');
      const footerTarget = document.getElementById('CartDrawer-Footer');
      if (footerSource != null && footerTarget) footerTarget.innerHTML = footerSource;
      this.bindOverlay();
      this.querySelector('cart-drawer-items')?.syncCartChrome?.(parsedState);
      if (typeof window.scheduleNeoniifyCartPreviews === 'function') {
        window.scheduleNeoniifyCartPreviews(this);
      }
    }
    return parsedState;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {}

customElements.define('cart-drawer-items', CartDrawerItems);
